import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Check, Building2, GraduationCap, Star } from "lucide-react";

const PLAN_FEATURES = {
  school_basic: [
    "Up to 25 students",
    "All training modules",
    "Instructor voices",
    "Student progress dashboard",
    "Tenant-isolated records",
  ],
  school_pro: [
    "Unlimited students & instructors",
    "Everything in School Basic",
    "Secure inter-school transfer handshake",
    "Live delivery recordings & recaps",
    "Priority support",
    "Custom branded characters",
  ],
  student: [
    "Personal access to all modules",
    "Self-paced learning",
    "Lesson recaps from your teacher",
    "Quiz scoring & revision",
  ],
};

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState({});

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const checkout = async (planId) => {
    if (!user) { window.location.href = "/"; return; }
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      alert("Could not start checkout. Make sure you're signed in.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--brand-primary)]">Subscription tiers</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-3">Pick the plan that fits your school.</h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Secure card payment, cancel any time. Test card{" "}
          <span className="font-mono text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded-sm">4242 4242 4242 4242</span>
          {" "}with any future date works in demo mode.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {["school_basic", "school_pro", "student"].map((id) => {
          const plan = plans[id];
          if (!plan) return null;
          const isFeatured = id === "school_pro";
          return (
            <div
              key={id}
              className={`relative bg-white rounded-sm p-8 transition-shadow ${
                isFeatured
                  ? "border-2 border-[var(--brand-primary)] shadow-md"
                  : "border border-slate-200 hover:shadow-md"
              }`}
              data-testid={`plan-${id}`}
            >
              {isFeatured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-sm bg-[var(--brand-primary)] text-[var(--brand-primary-fg)] text-[10px] font-bold uppercase tracking-[0.14em]">
                  <Star className="w-3 h-3" /> Most popular
                </div>
              )}
              <div className="flex items-center gap-2 mb-2 text-slate-500">
                {id.startsWith("school") ? <Building2 className="w-4 h-4" /> : <GraduationCap className="w-4 h-4" />}
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold">{plan.interval}</div>
              </div>
              <h3 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">{plan.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-heading font-bold text-5xl text-slate-900 tabular-nums">${plan.amount}</span>
                <span className="text-slate-500 text-sm">/ {plan.interval}</span>
              </div>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">{plan.description}</p>
              <ul className="mt-6 space-y-2.5">
                {(PLAN_FEATURES[id] || []).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-slate-700 text-sm">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => checkout(id)}
                className={`mt-8 w-full rounded-sm font-semibold ${
                  isFeatured
                    ? "bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)]"
                    : "bg-slate-900 hover:bg-slate-800 text-white"
                }`}
                data-testid={`subscribe-${id}-btn`}
              >
                Subscribe
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
